import { useState } from "react";
import { useFormContext } from "../../../../context/houseOwnerSignupFormContext";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
  FaEnvelope,
  FaLock,
  FaCity,
  FaMapMarkerAlt,
  FaGlobe,
  FaMailBulk,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { MdHome } from "react-icons/md";
import { StepFooterNav } from "../../shared/NavigationFooter";

export function Step2({ nextStep, preStep, currentStep, totalSteps }) {
  const { formData, handleChange } = useFormContext();
  const [confirmPassword, setConfirmPassword] = useState("");

  // password visibility
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    let newErrors = {};
    let valid = true;

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email || "")) {
      newErrors.email = "Please enter a valid email address.";
      valid = false;
    }
    if ((formData.password || "").length < 6) {
      newErrors.password = "Password must be at least 6 characters long.";
      valid = false;
    }
    if (formData.password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      valid = false;
    }
    if (!formData.address) {
      newErrors.address = "Address is required.";
      valid = false;
    }
    if (!formData.city) {
      newErrors.city = "City is required.";
      valid = false;
    }
    if (!formData.province) {
      newErrors.province = "Province is required.";
      valid = false;
    }
    if (!formData.country) {
      newErrors.country = "Country is required.";
      valid = false;
    }
    if (!formData.postalCode) {
      newErrors.postalCode = "Postal code is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleNext = () => {
    if (validateForm()) {
      nextStep();
    }
  };

  return (
    <div className="flex flex-col px-6 py-10">
      {/* Title */}
      <h2 className="text-2xl font-bold text-gray-900">Account & Address</h2>
      <p className="text-gray-600 mb-8">
        Set your login details and address information
      </p>

      <div className="space-y-6">
        {/* Email */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaEnvelope className="w-4 h-4" /> Email
          </Label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1">{errors.email}</p>
          )}
        </div>

        {/* Password */}
        <div className="relative">
          <Label className="flex items-center gap-2 mb-2">
            <FaLock className="w-4 h-4" /> Password
          </Label>
          <Input
            type={passwordVisible ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter password"
          />
          <button
            type="button"
            className="absolute right-3 top-[34px] text-gray-500"
            onClick={() => setPasswordVisible(!passwordVisible)}
          >
            {passwordVisible ? <FaEyeSlash /> : <FaEye />}
          </button>
          {errors.password && (
            <p className="text-xs text-red-500 mt-1">{errors.password}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="relative">
          <Label className="flex items-center gap-2 mb-2">
            <FaLock className="w-4 h-4" /> Confirm Password
          </Label>
          <Input
            type={confirmPasswordVisible ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
          />
          <button
            type="button"
            className="absolute right-3 top-[34px] text-gray-500"
            onClick={() =>
              setConfirmPasswordVisible(!confirmPasswordVisible)
            }
          >
            {confirmPasswordVisible ? <FaEyeSlash /> : <FaEye />}
          </button>
          {errors.confirmPassword && (
            <p className="text-xs text-red-500 mt-1">
              {errors.confirmPassword}
            </p>
          )}
        </div>

        {/* Separator for Address Section */}
        <div className="pt-6 border-t">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            Address Details
          </h3>
          <p className="text-gray-500 text-sm">
            Please provide your address information below.
          </p>
        </div>

        {/* Address */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <MdHome className="w-4 h-4" /> Address
          </Label>
          <Input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Street address"
          />
          {errors.address && (
            <p className="text-xs text-red-500 mt-1">{errors.address}</p>
          )}
        </div>

        {/* City */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaCity className="w-4 h-4" /> City
          </Label>
          <Input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="Your city"
          />
          {errors.city && (
            <p className="text-xs text-red-500 mt-1">{errors.city}</p>
          )}
        </div>

        {/* Province */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaMapMarkerAlt className="w-4 h-4" /> Province
          </Label>
          <Input
            type="text"
            name="province"
            value={formData.province}
            onChange={handleChange}
            placeholder="Your province"
          />
          {errors.province && (
            <p className="text-xs text-red-500 mt-1">{errors.province}</p>
          )}
        </div>

        {/* Country */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaGlobe className="w-4 h-4" /> Country
          </Label>
          <Input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleChange}
            placeholder="Your country"
          />
          {errors.country && (
            <p className="text-xs text-red-500 mt-1">{errors.country}</p>
          )}
        </div>

        {/* Postal Code */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaMailBulk className="w-4 h-4" /> Postal Code
          </Label>
          <Input
            type="text"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            placeholder="Postal Code"
          />
          {errors.postalCode && (
            <p className="text-xs text-red-500 mt-1">{errors.postalCode}</p>
          )}
        </div>
      </div>

      {/* Step Footer Navigation */}
      <StepFooterNav
        currentStep={currentStep}
        totalSteps={totalSteps}
        onBack={preStep}
        onNext={handleNext}
        nextDisabled={false}
      />
    </div>
  );
}
